import {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import {
  addMonths,
  differenceInDays,
  endOfMonth,
  endOfYesterday,
  format,
  isAfter,
  isBefore,
  isSameMonth,
  isWithinInterval,
  startOfMonth,
  startOfToday,
} from 'date-fns';
import clsx from 'clsx';
import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { useTranslation } from '@/i18n';
import { useLanguage } from '@/contexts/LanguageContext';
import { LOCALES_MAP } from '@/constants/localeMap';

enum DayFormatString {
  'fullDay' = 'iiii',
  'initial' = 'iiiii',
}

const DAY_FORMAT_CHANGE_BREAKPOINT = 768;

interface CircuitDetailsPricesCalendarCardProps {
  calculateMinimumPriceForDate: (date: Date) => number;
  nbParticipants: number;
  currency?: string;
  onDayClick?: (date: Date) => void;
  foldable?: boolean;
  defaultDate?: Date;
  // Dernière date de départ possible d'après les tarifs (null : aucune).
  lastPricedStartDate: Date | null;
  // Appelé avec le dernier jour du mois affiché, pour que la page calcule les
  // prix de ce mois s'ils ne le sont pas encore.
  onMonthChange?: (endOfDisplayedMonth: Date) => void;
}

const CircuitDetailsPricesCalendarCard = ({
  calculateMinimumPriceForDate,
  nbParticipants,
  onDayClick,
  currency = 'EUR',
  foldable = true,
  defaultDate = new Date(),
  lastPricedStartDate,
  onMonthChange,
}: CircuitDetailsPricesCalendarCardProps) => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const [isFolded, setIsFolded] = useState(false);

  const calendarRef = useRef<FullCalendar>(null);

  // Le calendrier ne montre aucune cellule pour un mois entièrement passé
  // (les jours antérieurs à aujourd'hui ne sont pas rendus). Si la date par
  // défaut (ex. date de départ enregistrée) est dans un mois passé, on ouvre
  // sur le mois courant pour éviter un calendrier « vide ».
  const initialMonthDate = useMemo(() => {
    const today = startOfToday();
    return !isSameMonth(defaultDate, today) && !isAfter(defaultDate, today)
      ? today
      : defaultDate;
  }, [defaultDate]);

  const [monthDate, setMonthDate] = useState<Date>(initialMonthDate);

  const isFirstMonth = useMemo(
    () => isSameMonth(startOfToday(), monthDate),
    [monthDate],
  );

  // On peut avancer tant que les tarifs couvrent une date de départ : au-delà,
  // le calendrier ne montrerait que des « — ».
  const isLastMonth = useMemo(
    () =>
      !isBefore(monthDate, startOfMonth(lastPricedStartDate ?? startOfToday())),
    [monthDate, lastPricedStartDate],
  );

  const goToMonth = (month: Date) => {
    setMonthDate(month);
    onMonthChange?.(endOfMonth(month));
  };

  useEffect(() => {
    onMonthChange?.(endOfMonth(initialMonthDate));
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (calendarRef.current && onDayClick !== undefined) {
      calendarRef.current.getApi().select(defaultDate);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CalendarDayCellContentMemo = useMemo(
    () =>
      memo(({ date }: { date: Date }) => (
        <CalendarDayCellContent
          date={date}
          calculateMinimumPriceForDate={calculateMinimumPriceForDate}
          monthDate={monthDate}
          currency={currency}
        />
      )),
    [calculateMinimumPriceForDate, monthDate, currency],
  );

  const isSelectable = useMemo(() => onDayClick !== undefined, [onDayClick]);
  const selectAllow = useCallback(
    ({ start, end }: { start: Date; end: Date }) =>
      differenceInDays(end, start) <= 1 && isAfter(start, endOfYesterday()),
    [],
  );
  const dayClick = useCallback(
    ({ date }: { date: Date }) => {
      if (onDayClick) onDayClick(date);
    },
    [onDayClick],
  );

  const [dayFormat, setDayFormat] = useState<DayFormatString>(
    window.innerWidth > DAY_FORMAT_CHANGE_BREAKPOINT
      ? DayFormatString.fullDay
      : DayFormatString.initial,
  );

  useEffect(() => {
    const handleResize = () => {
      setDayFormat(
        window.innerWidth > DAY_FORMAT_CHANGE_BREAKPOINT
          ? DayFormatString.fullDay
          : DayFormatString.initial,
      );
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Card className="p-4">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle>
          {t('circuits.editorial.calendar.selectDepartureDate')}
        </CardTitle>
        {foldable &&
          (isFolded ? (
            <ChevronDown
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(false)}
            />
          ) : (
            <ChevronUp
              className="size-5 hover:cursor-pointer"
              onClick={() => setIsFolded(true)}
            />
          ))}
      </CardHeader>
      {!isFolded && (
        <CardContent className="flex flex-col gap-4">
          <div className="flex gap-6 justify-between items-center px-6 py-4 border border-brand-100 rounded-lg">
            <StyledTooltipContainer
              tooltipMessage={t('circuits.editorial.calendar.previousMonth')}
            >
              <ArrowLeftIcon
                className={clsx('size-6', {
                  'hover:cursor-pointer stroke-black': !isFirstMonth,
                  'stroke-gray-400': isFirstMonth,
                })}
                onClick={() => {
                  if (isFirstMonth) return;
                  calendarRef.current?.getApi().prev();
                  goToMonth(addMonths(monthDate, -1));
                }}
              />
            </StyledTooltipContainer>
            <span className="text-lg font-normal leading-6 text-black">
              {format(monthDate, 'LLLL yyyy', {
                locale: LOCALES_MAP[currentLanguage],
              })}
            </span>
            <StyledTooltipContainer
              tooltipMessage={t('circuits.editorial.calendar.nextMonth')}
            >
              <ArrowRightIcon
                className={clsx('size-6', {
                  'hover:cursor-pointer stroke-black': !isLastMonth,
                  'stroke-gray-400': isLastMonth,
                })}
                onClick={() => {
                  if (isLastMonth) return;
                  calendarRef.current?.getApi().next();
                  goToMonth(addMonths(monthDate, 1));
                }}
              />
            </StyledTooltipContainer>
          </div>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            selectable={isSelectable}
            unselectAuto={false}
            dateClick={dayClick}
            selectAllow={selectAllow}
            fixedWeekCount={false}
            locale={currentLanguage}
            firstDay={1}
            headerToolbar={false}
            ref={calendarRef}
            height={'auto'}
            initialDate={initialMonthDate}
            dayHeaderContent={({ date }) =>
              format(date, dayFormat, {
                locale: LOCALES_MAP[currentLanguage],
              })
            }
            dayHeaderClassNames="!text-gray-800 !text-base !font-normal !border-0"
            dayCellContent={({ date }) => (
              <CalendarDayCellContentMemo date={date} />
            )}
            dayCellClassNames="!border-0"
          />
          <span className="text-sm xl:text-base text-gray-800">
            {t('circuits.editorial.calendar.pricingNote', {
              participants: nbParticipants,
            })}
          </span>
        </CardContent>
      )}
    </Card>
  );
};

export default CircuitDetailsPricesCalendarCard;

interface CalendarDayCellContentProps {
  date: Date;
  calculateMinimumPriceForDate: (date: Date) => number;
  monthDate: Date;
  currency?: string;
}

const CalendarDayCellContent = ({
  date,
  calculateMinimumPriceForDate,
  monthDate,
  currency = 'EUR',
}: CalendarDayCellContentProps) => {
  const { currentLanguage } = useLanguage();
  const isVerticalOrientation = useMediaQuery(600);

  const verticalStyles: CSSProperties | undefined = useMemo(
    () =>
      isVerticalOrientation
        ? { writingMode: 'vertical-lr', textOrientation: 'upright' }
        : undefined,
    [isVerticalOrientation],
  );

  if (
    !isWithinInterval(date, {
      start: startOfMonth(monthDate),
      end: endOfMonth(monthDate),
    }) ||
    !isAfter(date, endOfYesterday())
  )
    return null;

  const price = calculateMinimumPriceForDate(date);

  return (
    <div className="flex flex-col items-end gap-2 w-full border border-brand-100 rounded-lg px-1 md:px-2 py-4 hover:cursor-pointer">
      <span className="text-sm font-normal text-gray-600">
        {format(date, 'dd', { locale: LOCALES_MAP[currentLanguage] })}
      </span>
      <span
        className="text-sm md:text-base lg:text-lg xl:text-xl font-normal text-brand-600 break-all lg:break-normal"
        style={verticalStyles}
      >
        {price === Infinity
          ? '—'
          : `${formatPrice(price)} ${formatCurrency(currency)}`}
      </span>
    </div>
  );
};
